/**
 * Isaiah's World, Analytics & Error Monitoring
 *
 * Wraps PostHog for custom event tracking and error capture.
 * All tracking is opt-in friendly and respects user privacy.
 */

function getPostHog() {
  return window.posthog;
}

/**
 * Track a custom event.
 * @param {string} event, e.g., "game_started", "story_created"
 * @param {object} props, optional properties
 */
export function trackEvent(event, props = {}) {
  try {
    const ph = getPostHog();
    if (ph) ph.capture(event, props);
  } catch (err) {
    // Analytics must never break the app, but log at debug level so the
    // failure is still inspectable in DevTools.
    console.debug("[analytics] trackEvent failed:", event, err);
  }
}

/**
 * Identify a user (call on login/signup).
 * @param {string} userId
 * @param {object} traits, optional user traits
 */
export function identifyUser(userId, traits = {}) {
  try {
    const ph = getPostHog();
    if (ph) ph.identify(userId, traits);
  } catch (err) {
    console.debug("[analytics] identifyUser failed:", err);
  }
}

/**
 * Reset identity (call on logout).
 */
export function resetIdentity() {
  try {
    const ph = getPostHog();
    if (ph) ph.reset();
  } catch (err) {
    console.debug("[analytics] resetIdentity failed:", err);
  }
}

/**
 * Capture a frontend error for monitoring.
 * @param {Error|string} error
 * @param {object} context, optional context (page, action, etc.)
 */
export function captureError(error, context = {}) {
  try {
    const ph = getPostHog();
    if (ph) {
      ph.capture("$exception", {
        $exception_message: error?.message || String(error),
        $exception_stack: error?.stack || "",
        ...context,
      });
    }
  } catch (err) {
    console.debug("[analytics] captureError failed:", err);
  }
}

// ── Pre-defined event helpers for common flows ──

export const analytics = {
  // Auth
  signup: (method) => trackEvent("user_signed_up", { method }),
  login: (method) => trackEvent("user_logged_in", { method }),
  logout: () => trackEvent("user_logged_out"),

  // Learning
  lessonStarted: (subject, level) => trackEvent("lesson_started", { subject, level }),
  lessonCompleted: (subject, stars) => trackEvent("lesson_completed", { subject, stars_earned: stars }),
  quizCompleted: (subject, score) => trackEvent("quiz_completed", { subject, score }),

  // Games
  gameStarted: (game, mode) => trackEvent("game_started", { game, mode }),
  gameCompleted: (game, score, duration) => trackEvent("game_completed", { game, score, duration_seconds: duration }),

  // Stories
  storyCreated: (theme) => trackEvent("story_created", { theme }),
  storyShared: () => trackEvent("story_shared"),

  // Art
  artCreated: (style) => trackEvent("art_created", { style }),
  artShared: () => trackEvent("art_shared"),

  // AI Tutor
  tutorAsked: (subject) => trackEvent("tutor_question_asked", { subject }),

  // Social
  discoveryCollected: (world) => trackEvent("discovery_collected", { world }),
  achievementEarned: (name) => trackEvent("achievement_earned", { achievement: name }),
  trophyClaimed: (name) => trackEvent("trophy_claimed", { trophy: name }),

  // Parent
  childProfileCreated: () => trackEvent("child_profile_created"),
  parentSettingsUpdated: () => trackEvent("parent_settings_updated"),

  // Engagement
  pageViewed: (page) => trackEvent("$pageview", { page }),
  installPromptShown: () => trackEvent("pwa_install_prompt_shown"),
  installAccepted: () => trackEvent("pwa_install_accepted"),
  installDismissed: () => trackEvent("pwa_install_dismissed"),
};
